<script>
import axios from 'axios'
import { vMaska } from 'maska/vue'

export default {
    name: 'PayPage',

    directives: { maska: vMaska },

    props: {
        transaction: {
            type: Object,
            required: true
        },
        apiBase: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            step: 'otp',
            otp: '',
            loading: false,
            error: '',
            resultCode: null,
            resendTimer: 0,
            resendInterval: null,
            redirectUrl: null,
            redirectTimeout: null
        }
    },

    computed: {
        otpValid() {
            return this.otp.length >= 6
        },
        formattedAmount() {
            return Number(this.transaction.amount).toLocaleString('en-US')
        }
    },

    beforeUnmount() {
        clearInterval(this.resendInterval)
        clearTimeout(this.redirectTimeout)
    },

    methods: {
        async verifyOtp() {
            if (!this.otpValid || this.loading) return
            this.loading = true
            this.error = ''
            try {
                const res = await axios.post(`${this.apiBase}/w-confirm`, {
                    gtid: this.transaction.gtid,
                    pin: this.otp,
                })
                if (!res.data.success) {
                    this.error = this.statusMessage(res.data.resultCode)
                    this.loading = false
                    return
                }
                this.resultCode = res.data.resultCode
                this.step = 'success'

                if (res.data.redirectUrl) {
                    this.redirectUrl = res.data.redirectUrl
                    this.redirectTimeout = setTimeout(() => {
                        window.location.href = this.redirectUrl
                    }, 3000)
                }
            } catch (error) {
                const code = error.response?.data?.resultCode
                this.error = this.statusMessage(code)
                this.loading = false
            }
        },

        async resendOtp() {
            if (this.resendTimer > 0 || this.loading) return
            this.loading = true
            this.error = ''
            try {
                const res = await axios.post(`${this.apiBase}/w-resend-pin`, {
                    gtid: this.transaction.gtid,
                })
                if (!res.data.success) {
                    this.error = this.statusMessage(res.data.resultCode)
                    return
                }
                this.startResendTimer()
            } catch (error) {
                const code = error.response?.data?.resultCode
                this.error = this.statusMessage(code)
            } finally {
                this.loading = false
            }
        },

        startResendTimer() {
            this.resendTimer = 60
            clearInterval(this.resendInterval)
            this.resendInterval = setInterval(() => {
                if (this.resendTimer <= 0) return clearInterval(this.resendInterval)
                this.resendTimer--
            }, 1000)
        },

        statusMessage(code) {
            const messages = {
                601: 'Operation not found',
                602: 'Invalid PIN code',
                603: 'Operator error, please try again later',
                604: 'Expired PIN code',
                605: 'Insufficient funds or reservation error',
                606: 'Deduction error, please contact support',
                607: 'PIN resend requested too frequently',
                608: 'Not enough balance',
                609: 'Subscriber not found',
            }
            return messages[code] || 'Unknown error, please contact support'
        }
    }
}
</script>

<template>
    <div class="font-sans w-full min-h-screen flex flex-col">

        <!-- Header -->
        <header class="w-full flex flex-col items-center pt-14 pb-10">
            <p class="text-4xl text-white font-bold tracking-tight">tele<span class="text-3xl font-normal text-white/60">.pay</span></p>
            <p class="text-white/40 text-[11px] uppercase tracking-[0.25em] mt-3">Secure Payment</p>
        </header>

        <main class="flex-1 w-full flex justify-center px-6 pb-16">
            <div class="w-full max-w-[380px]">

                <!-- Transaction info -->
                <div class="text-center mb-10">
                    <p class="text-white/40 text-[11px] uppercase tracking-widest mb-3">Amount to pay</p>
                    <p class="text-4xl font-bold text-white">
<!--                        <span class="text-xl font-medium text-white/60">{{ transaction.currency }}</span> {{ formattedAmount }}-->
                        <span class="text-4xl font-medium text-white/60">$</span>{{ formattedAmount }}
                    </p>
                    <p v-if="transaction.description" class="text-sm text-white/50 mt-2">{{ transaction.description }}</p>
                </div>

                <div class="h-px w-full bg-gradient-to-r from-transparent via-white/15 to-transparent mb-10"></div>

                <!-- Success -->
                <Transition name="fade" mode="out-in">
                    <div v-if="step === 'success'" class="text-center space-y-4 py-6">
                        <div class="flex justify-center">
                            <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                                <svg class="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                                </svg>
                            </div>
                        </div>
                        <p class="font-medium text-white text-lg">Payment confirmed</p>
                        <p v-if="redirectUrl" class="text-white/40 text-xs flex items-center justify-center gap-2">
                            <svg class="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                            </svg>
                            Redirecting you back...
                        </p>
                    </div>

                    <!-- OTP form -->
                    <div v-else class="space-y-8">

                        <!-- Phone (read-only) -->
                        <div>
                            <label class="block text-[11px] uppercase tracking-widest font-medium text-white/40 mb-3">Phone number</label>
                            <div class="w-full relative">
                                <img src="/img/flag-400.png" alt="" class="max-w-[22px] absolute left-0 top-1/2 -translate-y-1/2 opacity-50">
                                <input
                                    :value="transaction.msisdn"
                                    type="tel"
                                    disabled
                                    class="w-full pl-8 pb-2 bg-transparent border-0 border-b border-white/15 text-base text-white/60 cursor-not-allowed focus:outline-none"
                                />
                            </div>
                        </div>

                        <!-- OTP -->
                        <div class="space-y-3">
                            <label class="text-[11px] uppercase tracking-widest font-medium text-white/40 block">SMS code</label>
                            <input
                                v-model="otp"
                                type="text"
                                inputmode="numeric"
                                maxlength="6"
                                :disabled="loading"
                                class="w-full pb-2 bg-transparent border-0 border-b border-white/20 text-2xl text-center tracking-[0.4em] text-white focus:outline-none focus:border-yellow-500 transition disabled:opacity-50"
                                @keyup.enter="verifyOtp"
                            />

                            <p v-if="resendTimer > 0" class="text-xs text-center text-white/40 pt-1">
                                Resend in {{ resendTimer }} sec.
                            </p>
                            <p v-else class="w-full text-xs font-semibold text-white/70 text-center pt-1">
                                Did not receive SMS?
                                <span
                                    @click="resendOtp"
                                    class="text-yellow-500 hover:underline hover:cursor-pointer">
                                    Try again
                                </span>
                            </p>
                        </div>

                        <p v-if="error" class="text-red-400 text-xs text-center">{{ error }}</p>

                        <button
                            :disabled="!otpValid || loading"
                            class="w-full py-3 px-4 bg-[#073e3b] text-white text-sm font-semibold tracking-wide rounded-full hover:bg-[#0a4d49] active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed"
                            @click="verifyOtp"
                        >
                            <span v-if="loading" class="inline-flex items-center justify-center gap-2">
                                <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                                </svg>
                                Verifying...
                            </span>
                            <span v-else>Confirm payment</span>
                        </button>

                    </div>
                </Transition>
            </div>
        </main>
    </div>
</template>

<style>
@import '../assets/tailwind.css';

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-enter-from {
    opacity: 0;
    transform: translateX(10px);
}
.fade-leave-to {
    opacity: 0;
    transform: translateX(-10px);
}
</style>
